import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import ApiService from "../../services/ApiService";
import Spinner from "@cs/components/src/Spinner";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "@cs/components/src/Banner";
import VariableInputForm from "../shared/VariableInputForm";
import { styled } from "styled-components";
import Button from "@cs/components/src/Button";
import { Play } from "react-feather";
import { useOrganizationQuery } from "../../hooks/useOrganizationQuery";
import Section from "@cs/components/src/Section";

interface Props {}

const RunWrapper = styled.div`
  display: flex;
  padding: 0 10px 15px;
`;

const StyledBanner = styled(Banner)`
  margin-left: 15px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  > * + * {
    margin-left: 5px;
  }
`;

const RunScriptForm = (_props: Props) => {
  const { scriptId, systemDomainId } = useParams();
  const { data: organizations } = useOrganizationQuery();
  const [input, setInput] = useState<{ variables: VariableInstance[] }>({
    variables: [],
  });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();

  useEffect(() => {
    setStatus("idle");
  }, [scriptId, systemDomainId]);

  const organizationName = organizations?.find(
    ({ value }) => value === systemDomainId
  )?.label;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["config", "script", scriptId],
    queryFn: async () => {
      const response = await ApiService.fetch<{
        data: ScriptConfigItem;
      }>("GET", `/api/config/script/${scriptId}`);
      setInput({
        variables: [
          ...input.variables,
          ...response.data.variables
            .filter(
              ({ defaultValue, id, key }) =>
                defaultValue &&
                !input.variables.some(
                  ({ variableConfigId }) => variableConfigId === (id ?? key)
                )
            )
            .map(({ id, key, defaultValue, dataType }) => ({
              variableConfigId: id ?? key,
              value: defaultValue,
              dataType,
            })),
        ],
      });
      return response.data;
    },
    enabled: !!scriptId,
  });

  const scriptMutation = useMutation({
    mutationFn: async () => {
      const response = await ApiService.fetch<{ scriptExecutionId: string }>(
        "POST",
        `/api/script_execution/run/script/${systemDomainId}/${scriptId}`,
        input
      );
      return response;
    },
    onError: () => {
      setStatus("error");
    },
    onSuccess: (data) => {
      setStatus("success");
      navigate(`${systemDomainId}/script/${data.scriptExecutionId}`);
    },
  });

  return isLoading ? (
    <Spinner />
  ) : isError ? (
    <Banner error="Error loading plugin" />
  ) : data ? (
    <Section
      title={
        <TitleRow>
          <span>{data.title}</span>
        </TitleRow>
      }
    >
      <VariableInputForm
        value={input}
        config={data}
        onChange={(value) => {
          setInput(value);
          setStatus("idle");
        }}
      />
      <RunWrapper>
        <Button
          type="success"
          label={`Run Script (${organizationName})`}
          icon={Play}
          onClick={() => scriptMutation.mutate()}
          loading={scriptMutation.isLoading}
        />
        <StyledBanner
          error={status === "error" ? "Error starting script" : false}
          success={status === "success" ? "Started script" : false}
        />
      </RunWrapper>
    </Section>
  ) : (
    <></>
  );
};

export default RunScriptForm;
